/**
 * The marketplace's Emotes category. An emote is a RADIO CALLOUT, not a dance: the tile shows
 * the line it plays as a quoted subtitle, and an owned emote gets a play button that previews
 * its voice line. The preview is a plain `new Audio()` (`previewEmoteVoice`) and fails silently —
 * the asset may 404 until the audio pipeline ships it, and a broken preview must never surface
 * as an error in a store screen.
 *
 * Equip is global, same shape as a charm. Backend contract per the marketplace rebuild brief
 * (2026-09-16); a failed catalogue fetch just hides this tab.
 */
import { button, confirmAction, icon } from '../ui';
import { formatNumber, t } from '../../i18n';
import {
  equipEmote,
  fetchEmotesCatalogue,
  previewEmoteVoice,
  purchaseEmote,
  type EmoteCatalogueView,
  type EmoteEntry,
} from '../emotes-api';
import { itemArt, type MarketItem } from './marketplace-tile';
import { EMOTES, emoteArtPath } from '../../../../../shared/src/emotes';
import { createMarketSection, type MarketSection } from './marketplace-section';

/**
 * The callout's own glyph — crossed flags for "need backup", an anchor for "holding here".
 *
 * Every emote drew the same megaphone until 2026-09-16, which made a shop of twelve identical
 * rows: the one thing a player is choosing between is what the callout MEANS, and the tile said
 * nothing about it. The painted megaphone stands in for an id whose art has not shipped.
 */
function calloutGlyph(entry: EmoteEntry, dimmed: boolean): HTMLElement {
  const definition = EMOTES[entry.id];
  const plate = (): HTMLElement => icon('sound', { size: 40, className: dimmed ? 'opacity-70' : '' });
  if (!definition) return plate();
  const art = itemArt(emoteArtPath(definition.art), plate, 40);
  if (dimmed) art.classList.add('opacity-80');
  return art;
}

/** A compact play button for an owned emote's voice line — never the tile's only control. */
function previewButton(entry: EmoteEntry): HTMLElement {
  const el = button('▶', {
    variant: 'ghost',
    className: 'px-2.5 min-w-[36px]',
    ariaLabel: t('shop.emotes.preview', { item: entry.name }),
    onClick: () => previewEmoteVoice(entry.voice),
  });
  return el;
}

function toItem(entry: EmoteEntry, act: (mutate: () => Promise<unknown>) => void, busy: boolean): MarketItem {
  return {
    key: `emote-${entry.id}`,
    category: 'emotes',
    name: entry.name,
    description: `"${entry.line}"`,
    art: calloutGlyph(entry, !entry.owned),
    price: { amount: entry.priceCoins, currency: entry.currency },
    owned: entry.owned,
    equipped: entry.equipped,
    affordable: entry.affordable,
    extra: entry.owned ? previewButton(entry) : undefined,
    action: !entry.owned
      ? {
          label: entry.affordable ? t('common.buy') : t('shop.needGems'),
          variant: entry.affordable ? 'primary' : 'ghost',
          disabled: busy || !entry.affordable,
          onClick: () =>
            void (async () => {
              const confirmed = await confirmAction({
                title: t('confirmDialog.buyTitle'),
                body: t('confirmDialog.buyBody', { item: entry.name, price: `${formatNumber(entry.priceCoins)} ${t('common.gems')}` }),
              });
              if (!confirmed) return;
              act(() => purchaseEmote(entry.id));
            })(),
        }
      : !entry.equipped
        ? {
            label: t('common.equip'),
            variant: 'secondary',
            disabled: busy,
            onClick: () =>
              void (async () => {
                const confirmed = await confirmAction({
                  title: t('confirmDialog.equipTitle'),
                  body: t('confirmDialog.equipBody', { item: entry.name }),
                });
                if (!confirmed) return;
                act(() => equipEmote(entry.id));
              })(),
          }
        : {
            label: t('common.remove'),
            variant: 'ghost',
            disabled: busy,
            onClick: () => act(() => equipEmote(0)),
          },
  };
}

export function createEmotesSection(): MarketSection {
  return createMarketSection<EmoteCatalogueView>({
    id: 'emotes',
    label: t('shop.emotes.title'),
    note: t('shop.emotes.note'),
    fetchCatalogue: fetchEmotesCatalogue,
    toItems: (catalogue, act, busy) => catalogue.emotes.map((entry) => toItem(entry, act, busy)),
  });
}
